import { useEffect, useMemo, useState } from 'react';
import { DEFAULT_COUNTRY_ISO, type FieldDef, type FieldOption } from '@jfh/intake-shared';
import { api } from '../../lib/api.js';

/** iQ lists fetched in the last minute, by lookup key (the Worker caches as long). */
const LIST_MAX_AGE_MS = 60 * 1000;
const listCache = new Map<string, { at: number; p: Promise<FieldOption[]> }>();
export function loadList(key: string): Promise<FieldOption[]> {
  const hit = listCache.get(key);
  if (hit && Date.now() - hit.at < LIST_MAX_AGE_MS) return hit.p;
  const p = api
    .getLookup(key)
    .then((r) => r.options)
    .catch(() => {
      listCache.delete(key); // try again next time
      return [];
    });
  listCache.set(key, { at: Date.now(), p });
  return p;
}

/**
 * What the canvas needs to draw the form as applicants see it: questions with
 * their iQ answer lists filled in (as the public form endpoint does), the
 * country list from iQ, and the country settings.
 */
export function useFormPreviewData(fields: FieldDef[]) {
  const keys = useMemo(
    () =>
      [...new Set(fields.flatMap((f) => (f.optionsSource?.type === 'iq' ? [f.optionsSource.key] : [])))].sort(),
    [fields],
  );
  const [lists, setLists] = useState<Record<string, FieldOption[]>>({});
  const [countryOptions, setCountryOptions] = useState<FieldOption[]>([]);
  const [countryConfig, setCountryConfig] = useState<{ disabled: string[]; default: string }>({
    disabled: [],
    default: DEFAULT_COUNTRY_ISO,
  });

  const keysJoined = keys.join('|');
  useEffect(() => {
    let live = true;
    for (const key of keys) {
      void loadList(key).then((options) => {
        if (live) setLists((cur) => (cur[key] === options ? cur : { ...cur, [key]: options }));
      });
    }
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysJoined]);

  useEffect(() => {
    let live = true;
    void loadList('countries').then((o) => live && setCountryOptions(o));
    api
      .getCountryConfig()
      .then((c) => live && setCountryConfig(c))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  /** Questions with iQ answers inlined as `choices` (which the renderer reads first). */
  const resolved = useMemo(
    () =>
      fields.map((f) => {
        if (f.optionsSource?.type !== 'iq' || f.choices?.length) return f;
        const options = lists[f.optionsSource.key];
        return options?.length ? { ...f, choices: options } : f;
      }),
    [fields, lists],
  );

  return { resolved, countryOptions, countryConfig };
}
